import { FC, useState, MouseEvent } from 'react';
import {
  Box,
  ClickAwayListener,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';

import { ThreeDotsIcon } from '../Icons';
import styles from './ThreeDotsMenu.module.scss';

export interface MenuItemConfig {
  id: string;
  label: string;
  icon: React.ReactElement;
  onClick: (event: MouseEvent) => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

export interface ThreeDotsMenuProps {
  menuItems: MenuItemConfig[];
  iconSize?: number;
  rootClassName?: string;
  className?: string;
  disabled?: boolean;
  iconClassName?: string;
}

const ThreeDotsMenu: FC<ThreeDotsMenuProps> = ({
  menuItems,
  iconSize = 24,
  rootClassName = '',
  className = '',
  disabled = false,
  iconClassName = '',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (disabled) return;

    if (open && anchorEl === event.currentTarget) {
      setAnchorEl(null);
      return;
    }

    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event?: MouseEvent | React.SyntheticEvent) => {
    if (event && 'stopPropagation' in event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleClickAway = (event: globalThis.MouseEvent | TouchEvent) => {
    const target = event.target as Node;
    if (anchorEl?.contains(target)) {
      return;
    }
    handleMenuClose();
  };

  const handleItemClick = (item: MenuItemConfig) => (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (!item.disabled) {
      item.onClick(event);
    }
    handleMenuClose(event);
  };

  return (
    <Box className={`${styles.menuRoot} ${rootClassName}`.trim()}>
      <Box
        className={`${styles.menuButton} ${className} ${iconClassName}`}
        onClick={handleMenuClick}
      >
        <ThreeDotsIcon size={iconSize} />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 1300 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper className={styles.menuPaper}>
            <MenuList
              disablePadding
              className={styles.menu}
            >
              {menuItems.map((item, index) => (
                <MenuItem
                  key={item.id}
                  onClick={handleItemClick(item)}
                  disabled={item.disabled}
                  className={`${styles.menuItem} ${item.variant === 'danger' ? styles.dangerItem : ''} ${
                    index < menuItems.length - 1 && menuItems.length >= 2 ? styles.hasBottomBorder : ''
                  }`}
                >
                  <ListItemIcon>
                    <Box className={`${styles.menuIcon} ${item.variant === 'danger' ? styles.dangerIcon : ''}`}>
                      {item.icon}
                    </Box>
                  </ListItemIcon>
                  <ListItemText>
                    <Typography
                      variant="h10"
                      sx={{
                        color: item.variant === 'danger' ? '#C8102E' : '#222B3A',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </ListItemText>
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default ThreeDotsMenu;
